import { useState, useRef } from 'react';
import { Shield, Loader2, WifiOff, Check, CloudOff, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

interface ReadinessStepProps {
  progressId: string;
  alreadyComplete: boolean;
  existingNotes: string;
  onComplete: () => void;
  /** Queued mutation callback — returns true on success or enqueue, false on terminal failure */
  onSubmitReadiness?: (notes: string) => Promise<boolean>;
  /** Current save state from parent */
  saveState?: string;
  /** Preview mode — no backend writes */
  previewMode?: boolean;
}

export default function ReadinessStep({ progressId, alreadyComplete, existingNotes, onComplete, onSubmitReadiness, saveState, previewMode }: ReadinessStepProps) {
  const [notes, setNotes] = useState(existingNotes);
  const [confident, setConfident] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (alreadyComplete) {
    return (
      <div className="space-y-4">
        <div className="bg-primary/10 rounded-xl p-6 text-center">
          <Shield className="w-12 h-12 text-primary mx-auto mb-3" />
          <h2 className="text-lg font-bold text-foreground">Readiness Confirmed</h2>
          <p className="text-sm text-muted-foreground mt-1">You've confirmed you're ready for the practical assessment.</p>
        </div>
        {existingNotes && (
          <div className="border border-border rounded-xl p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Your Notes</p>
            <p className="text-sm text-foreground whitespace-pre-wrap">{existingNotes}</p>
          </div>
        )}
        <Button onClick={onComplete} className="w-full h-12 text-base">Continue to Summary</Button>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (previewMode) {
      setSubmitted(true);
      onComplete();
      return;
    }
    setSubmitting(true);
    if (onSubmitReadiness) {
      const ok = await onSubmitReadiness(notes);
      setSubmitting(false);
      if (ok) {
        setSubmitted(true);
        onComplete();
      }
    } else {
      setSubmitting(false);
    }
  };

  const statusLabel = submitted
    ? <span className="text-xs text-primary flex items-center gap-1"><Check className="w-3 h-3" /> Submitted</span>
    : saveState === 'queued'
    ? <span className="text-xs text-amber-500 flex items-center gap-1"><CloudOff className="w-3 h-3" /> Offline queued</span>
    : saveState === 'syncing'
    ? <span className="text-xs text-primary flex items-center gap-1 animate-pulse">Syncing…</span>
    : saveState === 'error'
    ? <span className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="w-3 h-3" /> Needs attention</span>
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-foreground">Practical Readiness</h2>
        {statusLabel}
      </div>
      <p className="text-sm text-muted-foreground">
        Before your practical assessment, reflect on what you've learned. Your supervisor will review your readiness notes.
      </p>

      <div className="border border-border rounded-xl p-4 space-y-3">
        <p className="text-sm font-medium text-foreground">Any areas you'd like to practise more or questions for your assessor?</p>
        <Textarea value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="E.g., I'd like to practise garnish cuts with the mandoline, or I'm unsure about the speed rail order for weekend specials…"
          rows={4} className="text-sm" />
      </div>

      <label className="flex items-start gap-3 p-4 border border-border rounded-xl cursor-pointer hover:bg-muted/30 transition-colors">
        <input type="checkbox" checked={confident} onChange={e => setConfident(e.target.checked)}
          className="mt-0.5 w-5 h-5 rounded border-border accent-primary" />
        <div>
          <p className="text-sm font-medium text-foreground">I confirm that I feel ready for the practical assessment</p>
          <p className="text-xs text-muted-foreground mt-0.5">This does not commit you to an assessment date — it signals readiness to your supervisor.</p>
        </div>
      </label>

      <Button onClick={handleSubmit} disabled={submitting || !confident}
        className="w-full h-12 text-base">
        {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : 'Confirm Readiness'}
      </Button>
    </div>
  );
}
